// MosqueSelection.tsx
import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select, {
  components,
  createFilter,
  OptionProps,
  StylesConfig,
} from "react-select";
import { Badge, Icon, styles as ui } from "stories/Admin";
import styles from "./SuperAdminMosqueSelection.module.scss";
import { RoutePaths } from "enums/RoutePaths";
import { formatOrgNumber } from "../AdminLayout/menuConfig";

interface Mosque {
  orgNr: string;
  orgName: string;
  name?: string;
  // Only used for display.
  city?: string;
  logo?: string;
}

interface MosqueOption {
  value: string;
  label: string;
  orgName: string;
  name?: string;
  city?: string;
  logo?: string;
}

const MosqueOptionView = (props: OptionProps<MosqueOption, false>) => {
  const { data, isFocused } = props;
  return (
    <components.Option {...props}>
      <span className={`${styles.thumb} ${data.logo ? styles.thumbLogo : ""}`}>
        {data.logo ? (
          <img
            src={data.logo}
            alt=""
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <Icon name="mosque" size={18} />
        )}
      </span>
      <span className={styles.optText}>
        <b>{data.label}</b>
        <span className={styles.optMeta}>
          {formatOrgNumber(data.value)}
          {data.city ? ` · ${data.city}` : ""}
        </span>
      </span>
      {isFocused && <span className={styles.kbd}>Enter</span>}
    </components.Option>
  );
};

const selectStyles: StylesConfig<MosqueOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 52,
    borderRadius: 12,
    paddingLeft: 36,
    fontSize: 16,
    borderColor: state.isFocused ? "#1d7a52" : "#d5ded9",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(29,122,82,0.15)" : "none",
    "&:hover": { borderColor: state.isFocused ? "#1d7a52" : "#b9c7c1" },
  }),
  placeholder: (base) => ({ ...base, color: "#8a9c96", fontSize: 14 }),
  input: (base) => ({ ...base, color: "#0d2a33" }),
  menu: (base) => ({
    ...base,
    position: "static",
    boxShadow: "none",
    margin: "10px 0 0",
    border: 0,
  }),
  menuList: (base) => ({ ...base, padding: 0 }),
  option: (base, state) => ({
    ...base,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 12px",
    borderRadius: 12,
    cursor: "pointer",
    color: "#0d2a33",
    background: state.isFocused ? "#f1faf5" : "transparent",
    "&:active": { background: "#e1f4ea" },
  }),
  noOptionsMessage: (base) => ({ ...base, color: "#546a71", fontSize: 14 }),
};

const SuperAdminMosqueSelection: React.FC = () => {
  const {
    setSelectedOrgNumber,
    setMosqueLogo,
    setMosqueName,
    setSuperAdminMode,
    setSelectedOrgName,
  } = useAuth();
  const { execute } = useFetch<Mosque[]>();
  const [mosqueList, setMosqueList] = useState<Mosque[]>([]);
  const [inputValue, setInputValue] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMosques = async () => {
      const url = `/api/v1/mosques`;
      const result = await execute(url, "GET");
      if (result) {
        const sortedMosques = result.sort((a: Mosque, b: Mosque) =>
          a.orgName.localeCompare(b.orgName),
        );
        setMosqueList(sortedMosques);
      }
    };
    fetchMosques();
  }, []);

  const handleSelectChange = (selectedOption: any) => {
    setMosqueName("");
    setMosqueLogo("");
    sessionStorage.removeItem("mosqueName");
    sessionStorage.removeItem("mosqueLogo");

    if (selectedOption) {
      const selectedOrgNr = selectedOption.value;
      setSelectedOrgNumber(selectedOrgNr);
      setSelectedOrgName(selectedOption.orgName);
      sessionStorage.setItem("selectedOrgNumber", selectedOrgNr);
      sessionStorage.setItem("selectedOrgName", selectedOption.orgName);
      navigate("/");
    }
  };

  const handleSuperAdminMode = () => {
    setSuperAdminMode(true);
    sessionStorage.setItem("isSuperAdminMode", "true");
    navigate(RoutePaths.Overview);
  };

  const mosqueOptions: MosqueOption[] = mosqueList.map((mosque) => ({
    value: mosque.orgNr,
    label: (mosque.name || mosque.orgName)?.toUpperCase(),
    orgName: mosque.orgName,
    name: mosque.name,
    city: mosque.city,
    logo: mosque.logo,
  }));

  const customFilter = createFilter({
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: "any",
    stringify: (option) => {
      const data = option.data as Mosque;
      return `${data.orgName} ${data.name}`;
    },
  });

  const shownCount = inputValue
    ? mosqueOptions.filter((option) =>
        customFilter(
          { label: option.label, value: option.value, data: option },
          inputValue,
        ),
      ).length
    : mosqueOptions.length;

  return (
    <div className={`${ui.root} ${styles.container}`}>
      <div className={styles.content}>
        <span className={styles.brand}>
          <span className={styles.brandMark}>
            <Icon name="moon" size={24} strokeWidth={2.2} />
          </span>
          <span className={styles.brandName}>iBønn Admin</span>
          <Badge tone="gold">SUPERADMIN</Badge>
        </span>

        <div>
          <h1 className={ui.h1}>Hvor vil du jobbe i dag?</h1>
          <div className={ui.sub}>
            Velg en moské for å administrere den som superadmin, eller gå til
            superadmin-verktøyene.
          </div>
        </div>

        <section className={`${ui.card} ${styles.searchCard}`}>
          <div className={styles.selectWrap}>
            <span className={styles.searchIcon}>
              <Icon name="search" size={20} strokeWidth={2} />
            </span>
            <Select
              options={mosqueOptions}
              onChange={handleSelectChange}
              placeholder="Søk på navn eller organisasjonsnavn"
              filterOption={customFilter}
              noOptionsMessage={() => "Ingen moskeer funnet"}
              aria-label="Søk og velg moské"
              autoFocus
              menuIsOpen
              maxMenuHeight={340}
              inputValue={inputValue}
              onInputChange={(value) => setInputValue(value)}
              components={{
                Option: MosqueOptionView,
                DropdownIndicator: null,
                IndicatorSeparator: null,
              }}
              styles={selectStyles}
            />
          </div>
          <span className={styles.count}>
            {shownCount} av {mosqueOptions.length} moskeer
          </span>
        </section>

        <button
          type="button"
          className={`${ui.card} ${styles.superCard}`}
          onClick={handleSuperAdminMode}
        >
          <span className={styles.superIcon}>
            <Icon name="star" size={22} strokeWidth={2} />
          </span>
          <span className={styles.superText}>
            <b>Gå til superadmin</b>
            <span>Moskeer, brukere, bønnetider, innhold, AiBønn og system</span>
          </span>
          <Icon name="chevronRight" size={20} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default SuperAdminMosqueSelection;
